import React, { useEffect, useState } from 'react';
import { Box, Flex, Heading, SimpleGrid, Text, VStack, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { AppCard } from '../../../../components/ui';
import { extractEquipmentKyc, getEquipmentKyc } from '../../../../repositories/equipmentKyc.repository';
import { formatDateTimeDisplay } from '../../../../utils/personName';
import { KycActionButtons, KycDocumentSection, KycExtractedSection, useKycCrud } from './kycUi';

export default function EquipmentKycDetail({ id, embedded = false }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [row, setRow] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getEquipmentKyc(id);
        if (alive) setRow(data);
      } catch (err) {
        if (alive) setError(err?.message || 'Failed to load');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (row?.extractedData?.status !== 'pending') return undefined;
    const timer = setInterval(async () => {
      try {
        setRow(await getEquipmentKyc(id));
      } catch {
        // keep current row
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [id, row?.extractedData?.status]);

  const crud = useKycCrud({
    onSaved: (saved) => setRow(saved),
    onDeleted: () => navigate('/admin/equipments/public-kyc'),
  });

  if (loading) {
    return (
      <Text color="text.secondary" py={6}>
        Loading…
      </Text>
    );
  }
  if (error || !row) {
    return (
      <Text color="status.error" py={6}>
        {error || 'Not found'}
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        {!embedded ? <Heading size="md">Equipment KYC</Heading> : <Box />}
        <KycActionButtons
          canEdit={crud.canEdit}
          canDelete={crud.canDelete}
          onEdit={() => crud.setEditRow(row)}
          onDelete={() => crud.setDeleteRow(row)}
        />
      </Flex>
      <AppCard>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
          <Box>
            <Text fontSize="12px" color="text.secondary" fontWeight="600">
              Email
            </Text>
            <Text fontWeight="600">{row.email}</Text>
          </Box>
          <Box>
            <Text fontSize="12px" color="text.secondary" fontWeight="600">
              Phone
            </Text>
            <Text fontWeight="600">{row.phone}</Text>
          </Box>
          <Box>
            <Text fontSize="12px" color="text.secondary" fontWeight="600">
              Submitted
            </Text>
            <Text fontWeight="600">{formatDateTimeDisplay(row.submittedAt)}</Text>
          </Box>
        </SimpleGrid>
      </AppCard>
      <AppCard>
        <KycDocumentSection rcFiles={row.rcFiles} aadhaarFiles={row.aadhaarFiles} />
      </AppCard>
      <AppCard>
        <KycExtractedSection
          extractedData={row.extractedData}
          extracting={extracting}
          onExtract={async () => {
            if (extracting) return;
            setExtracting(true);
            try {
              const data = await extractEquipmentKyc(id);
              setRow(data);
            } catch (err) {
              toast({ title: err?.message || 'AI extraction failed', status: 'error' });
            } finally {
              setExtracting(false);
            }
          }}
        />
      </AppCard>
      {crud.dialogs}
    </VStack>
  );
}
